import type { IamApi } from '@/features/iam/api/iam-api'
import { Id } from '@/infra/id'
import type {
  GenerateSelfMagicLinkRequest,
  SelfMagicLinkDestinationResource,
} from '../api/resources/magic-link'
import type { SelfMagicLinkDestination } from './types'

export class MagicLinkStore {
  constructor(private readonly _api: IamApi) {}

  /**
   * Requests a magic link for the specified email address and workspace ID.
   */
  async request(workspaceId: Id, email: string): Promise<void> {
    return this._api.magicLinksV4.requestMagicLink({
      workspace_id: Id.assert(workspaceId),
      email,
    })
  }

  /**
   * Generates a magic link for the current user for the specified destination.
   */
  async generateSelfServiceMagicLink(
    workspaceId: Id,
    destination: SelfMagicLinkDestination,
  ): Promise<string> {
    let destinationResource: SelfMagicLinkDestinationResource
    switch (destination.type) {
      case 'Dashboard':
        destinationResource = { type: 'DASHBOARD' }
        break
      case 'JobDetails':
        destinationResource = {
          type: 'JOB_DETAILS',
          job_id: Id.assert(destination.jobId),
        }
        break
      default: {
        const _exhaustive: never = destination
        throw new Error(
          `Unhandled destination type: ${(_exhaustive as SelfMagicLinkDestination).type}`,
        )
      }
    }

    const request: GenerateSelfMagicLinkRequest = {
      workspace_id: Id.assert(workspaceId),
      destination: destinationResource,
    }
    const response = await this._api.magicLinksV4.generateSelfMagicLink(request)
    return response.url
  }
}
